import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Plus, Mail, Phone, Building2, Edit, UserPlus, MapPin, FileText, FolderKanban, CheckSquare, Globe } from 'lucide-react';

export const ViewClientDialog = ({ client, open, onOpenChange, onEdit, onQuickAction }) => {
  const navigate = useNavigate();
  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl">{client.name}</DialogTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm"><Plus className="h-4 w-4 mr-2" />Quick Actions</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onQuickAction('task', client)}>
                  <CheckSquare className="h-4 w-4 mr-2" />New Task
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onQuickAction('project', client)}>
                  <FolderKanban className="h-4 w-4 mr-2" />New Project
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onQuickAction('proposal', client)}>
                  <FileText className="h-4 w-4 mr-2" />New Proposal
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 text-xs rounded-full ${client.company_id ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
              {client.company_id ? 'Corporate' : 'Individual'}
            </span>
            {client.status && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">{client.status}</span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {client.position && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Designation</label>
                <p className="font-medium">{client.position}</p>
              </div>
            )}
            {client.company_name && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Company</label>
                <p className="font-medium flex items-center gap-2"><Building2 className="h-4 w-4" />{client.company_name}</p>
              </div>
            )}
            {client.email && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Email</label>
                <p className="font-medium flex items-center gap-2"><Mail className="h-4 w-4" />{client.email}</p>
              </div>
            )}
            {client.phone && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Phone</label>
                <p className="font-medium flex items-center gap-2"><Phone className="h-4 w-4" />{client.phone}</p>
              </div>
            )}
          </div>
          {client.business_address && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Business Address</label>
              <p className="font-medium flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5" />{client.business_address}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {client.gst_number && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">GST Number</label>
                <p className="font-medium">{client.gst_number}</p>
              </div>
            )}
            {client.pan_number && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">PAN Number</label>
                <p className="font-medium">{client.pan_number}</p>
              </div>
            )}
          </div>
          {client.notes && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Notes</label>
              <p className="font-medium">{client.notes}</p>
            </div>
          )}
          {client.custom_fields && Object.keys(client.custom_fields).length > 0 && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Custom Fields</label>
              <div className="space-y-1 mt-1">
                {Object.entries(client.custom_fields).map(([key, value]) => (
                  <div key={key} className="text-sm bg-muted p-2 rounded"><strong>{key}:</strong> {value}</div>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => { onOpenChange(false); onEdit(client); }}>
              <Edit className="h-4 w-4 mr-2" />Edit
            </Button>
            <Button variant="outline" onClick={() => navigate(`/clients/${client.client_id}`)}>
              View Full Details
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ViewCompanyDialog = ({ company, clients, open, onOpenChange, onEdit, onAddContact }) => {
  const navigate = useNavigate();
  if (!company) return null;

  const companyContacts = clients.filter(c => c.company_id === company.company_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{company.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            {company.industry && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Industry</label>
                <p className="font-medium">{company.industry}</p>
              </div>
            )}
            {company.website && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Website</label>
                <p className="font-medium">
                  <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline flex items-center gap-2">
                    <Globe className="h-4 w-4" />{company.website}
                  </a>
                </p>
              </div>
            )}
            {company.phone && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Phone</label>
                <p className="font-medium flex items-center gap-2"><Phone className="h-4 w-4" />{company.phone}</p>
              </div>
            )}
          </div>
          {company.business_address && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Business Address</label>
              <p className="font-medium flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5" />{company.business_address}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {company.gst_number && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">GST Number</label>
                <p className="font-medium">{company.gst_number}</p>
              </div>
            )}
            {company.pan_number && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">PAN Number</label>
                <p className="font-medium">{company.pan_number}</p>
              </div>
            )}
          </div>
          {company.custom_fields && Object.keys(company.custom_fields).length > 0 && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Custom Fields</label>
              <div className="space-y-1 mt-1">
                {Object.entries(company.custom_fields).map(([key, value]) => (
                  <div key={key} className="text-sm bg-muted p-2 rounded"><strong>{key}:</strong> {value}</div>
                ))}
              </div>
            </div>
          )}
          {/* Company Contacts */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-muted-foreground">Contacts ({companyContacts.length})</label>
              <Button size="sm" variant="outline" onClick={() => { onOpenChange(false); onAddContact(company); }}>
                <UserPlus className="h-4 w-4 mr-2" />Add Contact
              </Button>
            </div>
            <div className="space-y-2">
              {companyContacts.map((contact) => (
                <div key={contact.client_id} className="p-3 bg-muted rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-medium">{contact.name}</p>
                    <p className="text-sm text-muted-foreground">{contact.position || 'No designation'}</p>
                  </div>
                  <div className="text-sm text-muted-foreground">{contact.email}</div>
                </div>
              ))}
              {companyContacts.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No contacts linked to this company</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => { onOpenChange(false); onEdit(company); }}>
              <Edit className="h-4 w-4 mr-2" />Edit
            </Button>
            <Button variant="outline" onClick={() => navigate(`/companies/${company.company_id}`)}>
              View Full Details
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const EditClientDialog = ({ client, companies, open, onOpenChange, onSave }) => {
  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Contact</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Contact Name</label>
            <Input value={client.name} onChange={(e) => onSave({ ...client, name: e.target.value }, true)} />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Contact Type</label>
            <Select value={client.contact_type} onValueChange={(value) => onSave({ ...client, contact_type: value, company_id: value === 'individual' ? '' : client.company_id }, true)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="corporate">Corporate</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {client.contact_type === 'corporate' && (
            <div>
              <label className="text-sm font-medium mb-2 block">Company</label>
              <Select value={client.company_id || '__none__'} onValueChange={(value) => onSave({ ...client, company_id: value === '__none__' ? '' : value }, true)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.company_id} value={company.company_id}>{company.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Email</label>
              <Input value={client.email || ''} onChange={(e) => onSave({ ...client, email: e.target.value }, true)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Phone</label>
              <Input value={client.phone || ''} onChange={(e) => onSave({ ...client, phone: e.target.value }, true)} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Designation</label>
            <Input value={client.designation || ''} onChange={(e) => onSave({ ...client, designation: e.target.value }, true)} />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Business Address</label>
            <Textarea value={client.business_address || ''} onChange={(e) => onSave({ ...client, business_address: e.target.value }, true)} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">GST Number</label>
              <Input value={client.gst_number || ''} onChange={(e) => onSave({ ...client, gst_number: e.target.value }, true)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">PAN Number</label>
              <Input value={client.pan_number || ''} onChange={(e) => onSave({ ...client, pan_number: e.target.value }, true)} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Notes</label>
            <Textarea value={client.notes || ''} onChange={(e) => onSave({ ...client, notes: e.target.value }, true)} rows={2} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={() => onSave(client, false)}>Save Changes</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const EditCompanyDialog = ({ company, open, onOpenChange, onSave }) => {
  if (!company) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Company</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Company Name</label>
            <Input value={company.name} onChange={(e) => onSave({ ...company, name: e.target.value }, true)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Industry</label>
              <Input value={company.industry || ''} onChange={(e) => onSave({ ...company, industry: e.target.value }, true)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Website</label>
              <Input value={company.website || ''} onChange={(e) => onSave({ ...company, website: e.target.value }, true)} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Phone</label>
            <Input value={company.phone || ''} onChange={(e) => onSave({ ...company, phone: e.target.value }, true)} />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Business Address</label>
            <Textarea value={company.business_address || ''} onChange={(e) => onSave({ ...company, business_address: e.target.value }, true)} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">GST Number</label>
              <Input value={company.gst_number || ''} onChange={(e) => onSave({ ...company, gst_number: e.target.value }, true)} />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">PAN Number</label>
              <Input value={company.pan_number || ''} onChange={(e) => onSave({ ...company, pan_number: e.target.value }, true)} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={() => onSave(company, false)}>Save Changes</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
